import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Minus, Plus, Users } from "lucide-react";
import { PLANS, GROUP_DISCOUNT_TIERS, GROUP_DISCOUNT_MIN } from "../data/eventData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const discountFor = (qty) =>
  (GROUP_DISCOUNT_TIERS.find((t) => qty >= t.min && qty <= t.max) || { pct: 0 }).pct;

export default function InscriptionModal({ open, onOpenChange, defaultPlan = "presencial", defaultGroupSize = 1, contextSpeaker = null }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    role: "",
    city: "",
    state: "",
    plan: defaultPlan,
    group_size: 1,
    message: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Cálculo automático · mesma regra da seção de preços
  const activePlan = PLANS.find((p) => p.id === form.plan) || PLANS[0];
  const qty = Math.max(1, Number(form.group_size) || 1);
  const pct = discountFor(qty);
  const unit = activePlan.priceValue * (1 - pct / 100);
  const total = unit * qty;
  const setQty = (n) => update("group_size", Math.min(50, Math.max(1, n)));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        group_size: Number(form.group_size) || 1,
        source: "modal",
      });
      setDone(true);
      toast.success("Inscrição registrada · em breve nosso time entrará em contato.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar agora. Tente novamente.";
      toast.error(typeof msg === "string" ? msg : "Erro ao enviar inscrição.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({
      name: "",
      email: "",
      phone: "",
      organization: "",
      role: "",
      city: "",
      state: "",
      plan: defaultPlan,
      group_size: 1,
      message: "",
    });
  };

  // Sync state when reopening with different defaults / speaker context
  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        plan: defaultPlan,
        group_size: defaultGroupSize,
        message: contextSpeaker
          ? `Quero garantir minha vaga para acompanhar ${contextSpeaker.name} no PS27.`
          : f.message,
      }));
    }
  }, [open, defaultPlan, defaultGroupSize, contextSpeaker]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogContent
        className="max-w-2xl glass-strong border-white/10 bg-brand-base/95 text-white max-h-[92vh] overflow-y-auto"
        data-testid="inscription-modal"
        data-lenis-prevent
      >
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl sm:text-3xl text-white">
                Garanta sua vaga no Pregoeiros Summit 2027
              </DialogTitle>
              <DialogDescription className="text-brand-silver/80">
                Preencha os dados abaixo. Nossa equipe entrará em contato em até 24h para confirmar sua inscrição.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2" data-testid="inscription-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome completo *">
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Maria da Silva"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-name"
                  />
                </Field>
                <Field label="E-mail corporativo *">
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="seu@orgao.gov.br"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-email"
                  />
                </Field>
                <Field label="Telefone / WhatsApp *">
                  <Input
                    required
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(21) 90000-0000"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-phone"
                  />
                </Field>
                <Field label="Órgão / Empresa">
                  <Input
                    value={form.organization}
                    onChange={(e) => update("organization", e.target.value)}
                    placeholder="Tribunal, secretaria, autarquia..."
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-organization"
                  />
                </Field>
                <Field label="Cargo">
                  <Input
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    placeholder="Pregoeiro, gestor, advogado..."
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-role"
                  />
                </Field>
                <Field label="Município">
                  <Input
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder="Ex: Florianópolis"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="form-city"
                  />
                </Field>
                <Field label="Estado (UF)">
                  <Input
                    value={form.state}
                    onChange={(e) => update("state", e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="RJ"
                    maxLength={2}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 uppercase"
                    data-testid="form-state"
                  />
                </Field>
              </div>

              <Field label="Modalidade *">
                <RadioGroup
                  value={form.plan}
                  onValueChange={(v) => update("plan", v)}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  <PlanRadio
                    value="presencial"
                    title={`Presencial · ${PLANS[0].price}`}
                    sub={`${PLANS[0].badge} · Florianópolis/SC`}
                    selected={form.plan === "presencial"}
                  />
                  <PlanRadio
                    value="online"
                    title={`Online ao vivo · ${PLANS[1].price}`}
                    sub="Transmissão HD + 12 meses EAD"
                    selected={form.plan === "online"}
                  />
                </RadioGroup>
              </Field>

              {/* Quantidade + cálculo automático */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 space-y-4" data-testid="modal-qty-block">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-sans text-sm font-semibold text-brand-silver/80">
                    Quantidade de inscrições
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setQty(qty - 1)}
                      disabled={qty <= 1}
                      className="h-9 w-9 rounded-full border border-white/15 bg-white/[0.05] backdrop-blur-sm flex items-center justify-center text-white hover:border-white/40 hover:bg-white/[0.12] disabled:opacity-30 disabled:pointer-events-none transition-all"
                      aria-label="Diminuir quantidade"
                      data-testid="modal-qty-minus"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="font-display font-extrabold text-2xl text-white min-w-[2.25rem] text-center tabular-nums" data-testid="modal-qty-value">
                      {qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQty(qty + 1)}
                      className="h-9 w-9 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan flex items-center justify-center text-brand-deep hover:scale-105 transition-all"
                      aria-label="Aumentar quantidade"
                      data-testid="modal-qty-plus"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>

                <div
                  className={`flex items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-xs transition-all duration-300 ${
                    pct > 0
                      ? "bg-white/[0.10] border border-white/25 text-white backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
                      : "bg-white/[0.04] border border-white/10 text-brand-silver/60"
                  }`}
                  data-testid="modal-discount-note"
                >
                  <Users size={14} className="shrink-0" />
                  <span className="whitespace-nowrap">
                    {pct > 0
                      ? `Desconto de grupo aplicado: ${pct}% OFF`
                      : `Descontos especiais para grupos a partir de ${GROUP_DISCOUNT_MIN} inscritos`}
                  </span>
                </div>

                <div className="flex items-end justify-between gap-3 border-t border-white/10 pt-3.5">
                  <div>
                    <p className="font-sans text-xs font-semibold text-brand-silver/60">por inscrição</p>
                    {pct > 0 && (
                      <p className="text-xs text-brand-silver/50 line-through" data-testid="modal-unit-full">
                        {brl(activePlan.priceValue)}
                      </p>
                    )}
                    <p className="font-display font-extrabold text-2xl text-gradient-blue leading-tight" data-testid="modal-unit-price">
                      {brl(unit)}
                    </p>
                  </div>
                  {qty > 1 && (
                    <div className="text-right">
                      <p className="font-sans text-xs font-semibold text-brand-silver/60">total</p>
                      {pct > 0 && (
                        <p className="text-xs text-brand-silver/50 line-through" data-testid="modal-total-full">
                          {brl(activePlan.priceValue * qty)}
                        </p>
                      )}
                      <p className="font-display font-bold text-lg text-white leading-tight" data-testid="modal-total">
                        {brl(total)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Field label="Mensagem (opcional)">
                <Textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Algum detalhe que devemos saber? (Ex: nota de empenho, orçamento, dúvidas)"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                  data-testid="form-message"
                />
              </Field>

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:flex-1 hover:scale-[1.01]"
                  data-testid="form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Quero garantir minha vaga"
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-brand-silver/50 leading-relaxed text-center">
                Ao enviar, você concorda em receber comunicações sobre o evento. Seus dados estão protegidos.
              </p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center" data-testid="form-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-brand-cyan/15 ring-glow flex items-center justify-center mb-5">
              <CheckCircle2 className="text-brand-cyan" size={32} />
            </div>
            <h3 className="heading-md text-white mb-2">Inscrição registrada</h3>
            <p className="text-brand-silver/80 max-w-md mx-auto">
              Recebemos seus dados. Em até 24h um consultor PS27 entrará em contato pelo telefone informado para confirmar
              sua participação.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              className="btn-ghost mt-6"
              data-testid="form-close-success"
            >
              Fechar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-display text-xs font-semibold uppercase tracking-[0.14em] text-brand-glow/90">
      {label}
    </Label>
    {children}
  </div>
);

const PlanRadio = ({ value, title, sub, selected }) => (
  <label
    htmlFor={`plan-${value}`}
    className={`relative cursor-pointer rounded-xl px-4 py-3 transition-all ${
      selected
        ? "bg-white/[0.10] border border-white/30 backdrop-blur-md ring-glow"
        : "bg-white/5 border border-white/10 hover:border-white/25"
    }`}
    data-testid={`plan-option-${value}`}
  >
    <div className="flex items-center gap-3">
      <RadioGroupItem value={value} id={`plan-${value}`} className="border-brand-blue text-brand-blue" />
      <div>
        <div className="font-semibold text-white text-sm">{title}</div>
        <div className="text-xs text-brand-silver/70">{sub}</div>
      </div>
    </div>
  </label>
);
